/**
 * The interfaces tab of a FortiGate: a drawing of the front panel of the
 * unit (one icon per physical port, coloured by link state) above the list
 * of every configured interface, grouped by type, by role or by name.
 *
 *   Component class:   FortinetInterfacesComponent
 *   Selector:          app-fortinet-interfaces
 *   Data:              document `ports` (dashboard.build_ports)
 *
 * Hovering or selecting a port lights up the interfaces built on it (its
 * hardware switch, aggregate, VLANs). Selecting a row lights up the ports
 * under it. The port icons are the two <symbol> elements at the top of the
 * template: I can replace their content to use my own artwork.
 */
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  inject,
} from '@angular/core';

import {
  SEV_RANK, Sev, SortValue, TableSort, downloadCsv, fmtBps, fmtStamp,
} from './fortinet-shared';

/* ============================================================================
 * Shape of the document (document `ports`)
 * ========================================================================== */

export interface PortEntry {
  name: string;
  alias: string;
  /** cmdb type: physical, hard-switch, switch, aggregate, redundant, vlan,
   *  tunnel, vap-switch, loopback, ... */
  type: string;
  role: string;
  vdom: string;
  /** administrative status, null when not reported */
  admin_up: boolean | null;
  /** link state, null when nothing reports it */
  link: boolean | null;
  /** Mbit/s */
  speed: number | null;
  /** 1 full, 0 half */
  duplex: number | null;
  mac: string;
  ip: string;
  mask: string;
  /** static, dhcp, pppoe */
  mode: string;
  /** administrative access, in FortiOS order */
  access: string[];
  /** the clear-text protocols among `access` */
  insecure: string[];
  members: string[];
  member_of: { kind: 'hard-switch' | 'switch' | 'aggregate'; name: string } | null;
  /** VLAN parent */
  parent: string;
  vlanid: number | null;
  /** a port of the front panel */
  physical: boolean;
  /** a hardware switch port: drawn on the panel and as a member of its
   *  switch, but without a row of its own */
  switch_port: boolean;
  description: string;
  dhcp_clients: number;
  /** cmdb references, null when not reported */
  refs: number | null;
  rx_bps: number | null;
  tx_bps: number | null;
  /** has an address, a VLAN, references, DHCP clients, or is a member */
  in_use: boolean;
}

export interface PortsDoc {
  available: boolean;
  /** "FortiGate 30E", null when the unit doesn't report it */
  model: string | null;
  model_code: string | null;
  virtual: boolean;
  /** epoch ms of the newest configuration snapshot */
  at: number | null;
  interfaces: PortEntry[];
}

/** The fields of an issue this tab reads (id "iface-down:port3", ...). */
export interface PortIssue {
  id?: string;
  sev: Sev;
  title: string;
}

/* ============================================================================
 * View models
 * ========================================================================== */

type SlotState = 'up' | 'down' | 'disabled' | 'unknown' | 'empty';
/** legend entries: the states, plus a port that is down but needs no attention */
type LegendKey = SlotState | 'spare';
type SlotKind = 'rj45' | 'sfp';

interface Slot {
  /** port name; for an empty VM slot, "portN" */
  name: string;
  /** number printed next to the icon ('' when the group label says it) */
  label: string;
  kind: SlotKind;
  state: SlotState;
  port: PortEntry | null;
  issue: PortIssue | null;
}

interface FaceGroup {
  key: string;
  label: string;
  /** one row, or two (odd numbers on top, even ones below, like on the unit) */
  rows: Slot[][];
}

interface RowVm {
  port: PortEntry;
  /** 1 for a VLAN listed under its parent */
  depth: number;
  /** the members that are ports, drawn as small icons */
  memberPorts: Slot[];
  /** the other members, by name */
  memberNames: string[];
}

interface TableGroup {
  key: string;
  label: string;
  rows: RowVm[];
  /** interfaces in the group, VLANs listed under a parent included */
  count: number;
}

type ViewMode = 'type' | 'role' | 'name';

/** Port slots drawn for a FortiGate-VM, which can take up to 24 vNICs. */
const VM_SLOTS = 24;
/** A group with more ports than this is drawn on two rows. */
const ONE_ROW_MAX = 8;
/** Widest port tooltip, in px (same value as max-width of .port-tip in the CSS). */
const TIP_MAX_WIDTH = 290;
/** Smallest gap kept between the port tooltip and the edge of the card, in px. */
const TIP_EDGE_MARGIN = 8;

const TYPE_LABEL: Record<string, string> = {
  'physical': 'Physical interface',
  'hard-switch': 'Hardware switch',
  'switch': 'Software switch',
  'aggregate': '802.3ad aggregate',
  'redundant': 'Redundant interface',
  'vlan': 'VLAN',
  'emac-vlan': 'EMAC VLAN',
  'tunnel': 'Tunnel interface',
  'vap-switch': 'WiFi SSID',
  'wl-mesh': 'WiFi mesh',
  'loopback': 'Loopback interface',
  'vdom-link': 'VDOM link',
  'fext-wan': 'FortiExtender WAN',
  'zone': 'Zone',
};
const TYPE_ORDER = ['aggregate', 'redundant', 'hard-switch', 'physical', 'switch',
                    'vlan', 'emac-vlan', 'tunnel', 'vap-switch', 'wl-mesh',
                    'loopback', 'vdom-link', 'fext-wan', 'zone'];

const ROLE_LABEL: Record<string, string> = {
  lan: 'LAN', wan: 'WAN', dmz: 'DMZ', undefined: 'Undefined',
};
const ROLE_ORDER = ['wan', 'lan', 'dmz', 'undefined'];

const ACCESS_LABEL: Record<string, string> = {
  ping: 'PING', https: 'HTTPS', ssh: 'SSH', http: 'HTTP', telnet: 'TELNET',
  fgfm: 'FMG-Access', snmp: 'SNMP', 'radius-acct': 'RADIUS accounting',
  'probe-response': 'Probe response', fabric: 'Security Fabric',
  ftm: 'FTM', 'speed-test': 'Speed test', capwap: 'CAPWAP',
};

const ISSUE_KEYS = ['iface-down', 'iface-duplex', 'iface-util', 'iface-errors',
                    'iface-flap'];

/** Order of the panel groups: management, LAN side, DMZ, WAN, fibre. */
function groupRank(key: string): number {
  if (/^(mgmt|ha)/i.test(key)) return 0;
  if (/^dmz/i.test(key)) return 2;
  if (/^wan/i.test(key)) return 3;
  if (/^(x|sfp|s)$/i.test(key)) return 4;
  return 1;
}

/** Sorts names like a human would: "port2" before "port10". */
function natural(first: string, second: string): number {
  return first.localeCompare(second, undefined, { numeric: true, sensitivity: 'base' });
}

function isFibre(name: string): boolean {
  return /^(x|sfp|s)\d+$/i.test(name);
}

/** The number at the end of a name ("port12" -> "12"), or '' if none. */
function trailingNumber(name: string): string {
  const match = /(\d+)$/.exec(name);
  return match ? match[1] : '';
}

function slotState(port: PortEntry): SlotState {
  if (port.admin_up === false) return 'disabled';
  if (port.link === true) return 'up';
  if (port.link === false) return 'down';
  return 'unknown';
}

/** Status column order: the problems first (down, then disabled), then up.
 *  A link nothing reports gets no rank, so it goes to the bottom. */
const STATUS_RANK: Record<SlotState, number | null> = {
  down: 0, disabled: 1, up: 2, unknown: null, empty: null,
};

/** The value a row of the interface list is sorted on, for a column. */
function portSortValue(row: RowVm, key: string): SortValue {
  const port = row.port;
  switch (key) {
    case 'status':
      return STATUS_RANK[slotState(port)];
    // 0 is half duplex, so those come first when ascending
    case 'duplex':
      return port.duplex;
    case 'name':
      return port.name;
    case 'type':
      return TYPE_LABEL[port.type] ?? port.type;
    case 'members':
      // how many members; none at all goes to the bottom
      return port.members.length || null;
    case 'ip':
      return port.ip && port.ip !== '0.0.0.0' ? port.ip : null;
    case 'access': {
      if (!port.access.length) return null;
      // The clear-text protocols count much more than the others, so the
      // interfaces with telnet or http come first, and then the ones with
      // the most protocols open.
      return port.insecure.length * 100 + port.access.length;
    }
    case 'dhcp':
      return port.dhcp_clients || null;
    case 'refs':
      return port.refs;
    default:
      return null;
  }
}

/** "1 Gbps", with " half duplex" added when it is (unless withDuplex is
 *  false: the list has its own Duplex column). */
function fmtSpeed(port: PortEntry, withDuplex = true): string {
  if (!port.speed) return '';
  let text: string;
  if (port.speed >= 1000) {
    // the + drops a useless ".0" ("1 Gbps", but "2.5 Gbps")
    text = `${+(port.speed / 1000).toFixed(1)} Gbps`;
  } else {
    text = `${port.speed} Mbps`;
  }
  if (withDuplex && port.duplex === 0) {
    return `${text} half duplex`;
  }
  return text;
}

@Component({
  selector: 'app-fortinet-interfaces',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './fortinet-interfaces.component.html',
  // The dashboard stylesheet brings the Clarity look (cards, tables,
  // labels), this one only adds the panel and the grouped table.
  styleUrls: ['./fortinet-dashboard.component.css',
              './fortinet-interfaces.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FortinetInterfacesComponent implements OnChanges {
  @Input() ports: PortsDoc | null = null;
  /** open issues of the device: a port with one gets a marker */
  @Input() issues: PortIssue[] = [];
  /** panel title when the unit doesn't report its model */
  @Input() fallbackTitle = 'FortiGate';

  private host = inject(ElementRef<HTMLElement>);

  /** unique prefix for the SVG symbol ids of this instance */
  readonly uid = 'fgp-' + Math.random().toString(36).slice(2, 8);

  // ---- panel ----
  title = '';
  face: FaceGroup[] = [];
  portCount = 0;
  portsUp = 0;
  portsUnknown = 0;
  /** states present on the panel, for the legend */
  legend: LegendKey[] = [];

  // ---- table ----
  view: ViewMode = 'type';
  filter = '';
  groups: TableGroup[] = [];
  shownCount = 0;
  rowCount = 0;
  collapsed = new Set<string>();
  asOf = '';
  /** Column sort of the list. It sorts inside each group, and the VLANs
   *  stay under their parent (sorted between themselves). By name when the
   *  tab opens. */
  readonly sort = new TableSort('name', true, ['members', 'access', 'dhcp', 'refs']);
  /** The Duplex column only shows when an interface is in half duplex:
   *  otherwise it would be a column of "full" and "-". */
  hasHalfDuplex = false;

  // ---- link between the panel and the table ----
  /** interface under the mouse (port or row) */
  hovered: string | null = null;
  /** interface selected by a click, kept across refreshes */
  selected: string | null = null;
  litPorts = new Set<string>();
  litRows = new Set<string>();

  // ---- port tooltip ----
  tip: { slot: Slot; x: number; y: number; below: boolean } | null = null;

  private byName = new Map<string, PortEntry>();
  private issueOf = new Map<string, PortIssue>();

  readonly views: { id: ViewMode; label: string }[] = [
    { id: 'type', label: 'By type' },
    { id: 'role', label: 'By role' },
    { id: 'name', label: 'Alphabetically' },
  ];
  readonly legendText: Record<LegendKey, string> = {
    up: 'Link up', down: 'Link down', disabled: 'Disabled',
    unknown: 'Link not reported', empty: 'No vNIC',
    spare: 'Link down, spare or switch port',
  };
  readonly legendCls: Record<LegendKey, string> = {
    up: 'st-up', down: 'st-down', disabled: 'st-disabled', unknown: 'st-unknown',
    empty: 'st-empty', spare: 'st-down spare',
  };

  ngOnChanges(): void {
    const list = this.ports?.interfaces ?? [];

    this.byName = new Map();
    for (const port of list) {
      this.byName.set(port.name, port);
    }

    // The worst interface issue of each port, found from the issue id
    // ("iface-down:port3" -> port3)
    this.issueOf = new Map();
    for (const issue of this.issues ?? []) {
      const id = issue.id ?? '';
      const colonIndex = id.indexOf(':');
      if (colonIndex < 0 || !ISSUE_KEYS.includes(id.slice(0, colonIndex))) {
        continue;
      }
      const portName = id.slice(colonIndex + 1);
      const previous = this.issueOf.get(portName);
      if (!previous || SEV_RANK[issue.sev] < SEV_RANK[previous.sev]) {
        this.issueOf.set(portName, issue);
      }
    }

    // forget a selection or hover on an interface that disappeared
    if (this.selected && !this.byName.has(this.selected)) {
      this.selected = null;
    }
    if (this.hovered && !this.byName.has(this.hovered)) {
      this.hovered = null;
    }

    this.hasHalfDuplex = false;
    for (const port of list) {
      if (port.duplex === 0) {
        this.hasHalfDuplex = true;
      }
    }

    this.title = this.ports?.model || this.fallbackTitle;
    this.asOf = this.ports?.at ? fmtStamp(this.ports.at) : '';
    this.buildFace(list);
    this.buildTable();
    this.updateLit();
    this.tip = null;
  }

  /* ---------------------------------------------------------------- panel */

  private slotOf(port: PortEntry, label: string): Slot {
    return {
      name: port.name,
      label,
      kind: isFibre(port.name) ? 'sfp' : 'rj45',
      state: slotState(port),
      port,
      issue: this.issueOf.get(port.name) ?? null,
    };
  }

  private buildFace(list: PortEntry[]): void {
    const physicalPorts = list.filter((port) => port.physical);
    const groups = new Map<string, { label: string; slots: Slot[] }>();

    const addToGroup = (key: string, label: string, slot: Slot): void => {
      if (!groups.has(key)) {
        groups.set(key, { label, slots: [] });
      }
      groups.get(key)!.slots.push(slot);
    };

    const isVm = !!this.ports?.virtual;
    const vmPorts = new Map<number, PortEntry>();
    for (const port of physicalPorts) {
      const vmMatch = /^port(\d+)$/i.exec(port.name);
      if (isVm && vmMatch) {
        // on a VM I draw every vNIC slot later, in order
        vmPorts.set(+vmMatch[1], port);
        continue;
      }
      // A hardware switch port goes with its switch ("LAN 1 2 3 4"),
      // otherwise the name without its number is the group ("wan1" in WAN)
      let switchName = '';
      if (port.member_of?.kind === 'hard-switch') {
        switchName = port.member_of.name;
      }
      const prefix = port.name.replace(/\d+$/, '') || port.name;
      const key = switchName || prefix;
      const number = trailingNumber(port.name);
      const groupLabel = key.toLowerCase() === 'port' ? '' : key.toUpperCase();
      // no number: I print the full name, but only for switch ports
      const slotLabel = number || (switchName ? port.name : '');
      addToGroup(key, groupLabel, this.slotOf(port, slotLabel));
    }

    if (isVm && vmPorts.size) {
      // every vNIC slot, like on the FortiGate-VM panel
      const highest = Math.max(VM_SLOTS, ...vmPorts.keys());
      // rounded up to an even count so the two rows have the same length
      const slotCount = highest + (highest % 2);
      for (let slotNumber = 1; slotNumber <= slotCount; slotNumber++) {
        const port = vmPorts.get(slotNumber);
        if (port) {
          addToGroup('port', '', this.slotOf(port, String(slotNumber)));
        } else {
          addToGroup('port', '', {
            name: `port${slotNumber}`, label: String(slotNumber), kind: 'rj45',
            state: 'empty', port: null, issue: null,
          });
        }
      }
    }

    const sortedGroups = [...groups.entries()];
    sortedGroups.sort(([firstKey], [secondKey]) =>
      groupRank(firstKey) - groupRank(secondKey) || natural(firstKey, secondKey));

    this.face = [];
    for (const [key, group] of sortedGroups) {
      const slots = group.slots.sort((first, second) => natural(first.name, second.name));
      if (slots.length <= ONE_ROW_MAX) {
        this.face.push({ key, label: group.label, rows: [slots] });
        continue;
      }
      // two rows: odd positions on top, even ones below
      const topRow = slots.filter((_slot, index) => index % 2 === 0);
      const bottomRow = slots.filter((_slot, index) => index % 2 === 1);
      this.face.push({ key, label: group.label, rows: [topRow, bottomRow] });
    }

    this.portCount = physicalPorts.length;
    this.portsUp = physicalPorts.filter((port) => slotState(port) === 'up').length;
    this.portsUnknown = physicalPorts.filter((port) => slotState(port) === 'unknown').length;

    const states = new Set<LegendKey>();
    for (const group of this.face) {
      for (const row of group.rows) {
        for (const slot of row) {
          if (slot.state === 'down' && this.isSpare(slot)) {
            states.add('spare');
          } else {
            states.add(slot.state);
          }
        }
      }
    }
    const legendOrder: LegendKey[] = ['up', 'down', 'spare', 'disabled', 'unknown', 'empty'];
    this.legend = legendOrder.filter((state) => states.has(state));
  }

  /* ---------------------------------------------------------------- table */

  setView(view: ViewMode): void {
    if (this.view === view) return;
    this.view = view;
    this.buildTable();
  }

  setFilter(query: string): void {
    this.filter = query;
    this.buildTable();
  }

  /** A click on a column title of the list. */
  sortBy(key: string): void {
    this.sort.toggle(key);
    this.buildTable();
  }

  toggleGroup(key: string): void {
    if (this.collapsed.has(key)) {
      this.collapsed.delete(key);
    } else {
      this.collapsed.add(key);
    }
  }

  private matches(port: PortEntry, query: string): boolean {
    if (!query) return true;
    const searchable = [port.name, port.alias, port.ip, port.type, this.typeLabel(port.type),
                        port.role, port.description, port.vdom, ...port.members];
    return searchable.join(' ').toLowerCase().includes(query);
  }

  private rowOf(port: PortEntry, depth: number): RowVm {
    const memberPorts: Slot[] = [];
    const memberNames: string[] = [];
    for (const memberName of port.members) {
      const member = this.byName.get(memberName);
      if (member?.physical) {
        const label = trailingNumber(memberName) || memberName;
        memberPorts.push(this.slotOf(member, label));
      } else {
        memberNames.push(memberName);
      }
    }
    return { port, depth, memberPorts, memberNames };
  }

  /** The group a port goes in, for the current view. */
  private groupKeyOf(port: PortEntry): string {
    if (this.view === 'type') {
      return TYPE_LABEL[port.type] ? port.type : 'other';
    }
    if (this.view === 'role') {
      return ROLE_LABEL[port.role] ? port.role : 'undefined';
    }
    return 'all';
  }

  private groupLabelOf(key: string): string {
    if (this.view === 'type') return TYPE_LABEL[key] ?? 'Other';
    if (this.view === 'role') return ROLE_LABEL[key];
    return '';
  }

  private buildTable(): void {
    const query = this.filter.trim().toLowerCase();
    // the hardware switch ports only show on the panel
    const all = (this.ports?.interfaces ?? []).filter((port) => !port.switch_port);
    this.rowCount = all.length;

    const names = new Set<string>();
    for (const port of all) {
      names.add(port.name);
    }

    // VLANs go under their parent, except in the alphabetical view
    const nest = this.view !== 'name';
    const children = new Map<string, PortEntry[]>();
    const topLevel: PortEntry[] = [];
    for (const port of all) {
      if (nest && port.parent && names.has(port.parent)) {
        if (!children.has(port.parent)) {
          children.set(port.parent, []);
        }
        children.get(port.parent)!.push(port);
      } else {
        topLevel.push(port);
      }
    }

    let order: string[];
    if (this.view === 'type') {
      order = [...TYPE_ORDER, 'other'];
    } else if (this.view === 'role') {
      order = ROLE_ORDER;
    } else {
      order = ['all'];
    }

    // Each group first gets its parents (VLANs set aside), sorted by
    // the column. The VLANs are added under their parent after that.
    const parentsByGroup = new Map<string, RowVm[]>();
    const kidsByParent = new Map<string, RowVm[]>();
    let shown = 0;
    // by name first, so the rows with the same value in the sorted
    // column stay in name order
    topLevel.sort((first, second) => natural(first.name, second.name));
    for (const port of topLevel) {
      const kids = children.get(port.name) ?? [];
      kids.sort((first, second) => natural(first.name, second.name));
      const kidsShown = kids.filter((kid) => this.matches(kid, query));
      // a parent stays when one of its VLANs matches, so the VLAN has context
      if (!this.matches(port, query) && !kidsShown.length) {
        continue;
      }

      const key = this.groupKeyOf(port);
      if (!parentsByGroup.has(key)) {
        parentsByGroup.set(key, []);
      }
      parentsByGroup.get(key)!.push(this.rowOf(port, 0));

      const kidRows: RowVm[] = [];
      for (const kid of kidsShown) {
        kidRows.push(this.rowOf(kid, 1));
      }
      kidsByParent.set(port.name, kidRows);
      shown += 1 + kidsShown.length;
    }

    this.groups = [];
    for (const key of order) {
      const parents = parentsByGroup.get(key);
      if (!parents) {
        continue;
      }
      const group: TableGroup = { key, label: this.groupLabelOf(key), rows: [], count: 0 };
      for (const parent of this.sort.apply(parents, portSortValue)) {
        group.rows.push(parent);
        const kidRows = kidsByParent.get(parent.port.name) ?? [];
        for (const kid of this.sort.apply(kidRows, portSortValue)) {
          group.rows.push(kid);
        }
        group.count += 1 + kidRows.length;
      }
      this.groups.push(group);
    }
    this.shownCount = shown;
  }

  exportCsv(): void {
    const rows: unknown[][] = [];
    for (const group of this.groups) {
      for (const row of group.rows) {
        const port = row.port;
        const accessLabels = port.access.map((access) => this.accessLabel(access));
        rows.push([group.label, port.name, port.alias, this.typeLabel(port.type), port.role,
                   this.statusText(port), fmtSpeed(port), port.members.join(' '),
                   this.ipText(port), port.mode, accessLabels.join(' '),
                   port.dhcp_clients, port.refs ?? '', port.mac, port.vdom]);
      }
    }
    downloadCsv('fortigate-interfaces',
                ['Group', 'Name', 'Alias', 'Type', 'Role', 'Status', 'Speed',
                 'Members', 'IP/Netmask', 'Addressing', 'Administrative access',
                 'DHCP clients', 'Ref.', 'MAC', 'VDOM'], rows);
  }

  /* -------------------------------------- link between panel and table */

  /** The ports under an interface: itself, its members, its VLAN parent.
   *  `seen` stops me from looping forever on a weird config. */
  private portsUnder(name: string, seen = new Set<string>()): string[] {
    if (seen.has(name)) return [];
    seen.add(name);
    const port = this.byName.get(name);
    if (!port) return [];
    if (port.physical) return [name];

    const result: string[] = [];
    for (const memberName of port.members) {
      result.push(...this.portsUnder(memberName, seen));
    }
    if (port.parent) {
      result.push(...this.portsUnder(port.parent, seen));
    }
    return result;
  }

  /** The interfaces built on a port: its switch or aggregate, and the
   *  VLANs on any of them. */
  private rowsOver(name: string): Set<string> {
    const chain = new Set<string>();
    // climb up: port -> its switch/aggregate -> ...
    let current: PortEntry | undefined = this.byName.get(name);
    while (current && !chain.has(current.name)) {
      chain.add(current.name);
      if (current.member_of) {
        current = this.byName.get(current.member_of.name);
      } else {
        current = undefined;
      }
    }
    // software switches that list one of these as a member
    for (const port of this.byName.values()) {
      if (port.members.some((memberName) => chain.has(memberName))) {
        chain.add(port.name);
      }
    }
    // and the VLANs on top of any of them
    for (const port of this.byName.values()) {
      if (port.parent && chain.has(port.parent)) {
        chain.add(port.name);
      }
    }
    return chain;
  }

  private updateLit(): void {
    const focus = this.hovered ?? this.selected;
    this.litPorts = new Set();
    this.litRows = new Set();
    if (!focus) return;
    const port = this.byName.get(focus);
    if (!port) return;

    if (port.physical) {
      this.litPorts.add(focus);
      this.litRows = this.rowsOver(focus);
    } else {
      this.litRows.add(focus);
      for (const portName of this.portsUnder(focus)) {
        this.litPorts.add(portName);
      }
    }
  }

  hover(name: string | null): void {
    if (this.hovered === name) return;
    this.hovered = name;
    this.updateLit();
  }

  /** Click on a port or a row: selects it (a second click clears it). */
  select(name: string, reveal: 'row' | 'port' | null = null): void {
    this.selected = this.selected === name ? null : name;
    this.updateLit();
    if (!this.selected || !reveal) return;

    // scroll the first lit row (or port) into view, once Angular has
    // updated the page
    setTimeout(() => {
      const root = this.host.nativeElement as HTMLElement;
      const selector = reveal === 'row' ? '.if-row.lit' : '.port.lit';
      const element = root.querySelector(selector) as HTMLElement | null;
      element?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }

  clearSelection(): void {
    this.selected = null;
    this.updateLit();
  }

  showTip(slot: Slot, element: HTMLElement): void {
    this.hover(slot.port ? slot.name : null);
    const wrap = (this.host.nativeElement as HTMLElement).querySelector('.face-wrap');
    if (!wrap) return;

    const wrapBox = wrap.getBoundingClientRect();
    const portBox = element.getBoundingClientRect();
    // under the port when it sits in the top part of the panel
    const below = portBox.top - wrapBox.top < 110;
    // The tip is centred on the port, but never goes past the left or right
    // edge of the card (a port at the end of a row on a small screen):
    // half of the widest tip (290px, see the CSS), or of the card if narrower.
    const portMiddle = portBox.left - wrapBox.left + portBox.width / 2;
    const halfTip = Math.min(TIP_MAX_WIDTH, wrapBox.width - TIP_EDGE_MARGIN * 2) / 2;
    const leftmost = halfTip + TIP_EDGE_MARGIN;
    const rightmost = wrapBox.width - halfTip - TIP_EDGE_MARGIN;
    const tipX = Math.min(Math.max(portMiddle, leftmost), rightmost);
    let tipY: number;
    if (below) {
      tipY = portBox.bottom - wrapBox.top + 6;
    } else {
      tipY = portBox.top - wrapBox.top - 6;
    }
    this.tip = { slot, x: tipX, y: tipY, below };
  }

  hideTip(): void {
    this.tip = null;
    this.hover(null);
  }

  /** Escape hides the port tooltip without moving the focus (APG tooltip
   *  pattern), for example when it covers what the user wants to read. */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.tip) {
      this.hideTip();
    }
  }

  /* ---------------------------------------------------------- template */

  typeLabel(type: string): string {
    return TYPE_LABEL[type] ?? (type ? type : 'Other');
  }

  accessLabel(access: string): string {
    return ACCESS_LABEL[access] ?? access.toUpperCase();
  }

  roleLabel(role: string): string {
    return ROLE_LABEL[role] ?? '';
  }

  ipText(port: PortEntry): string {
    if (!port.ip) return '';
    if (port.mask) return `${port.ip}/${port.mask}`;
    return port.ip;
  }

  hasIp(port: PortEntry): boolean {
    return !!port.ip && port.ip !== '0.0.0.0';
  }

  statusText(port: PortEntry): string {
    switch (slotState(port)) {
      case 'disabled': return 'disabled';
      case 'up': return 'up';
      case 'down': return 'down';
      default: return '';
    }
  }

  /** Label class of the status column. A spare port that is down is not
   *  an alarm. */
  statusCls(port: PortEntry): string {
    switch (slotState(port)) {
      case 'up': return 'label-success';
      case 'down': return port.in_use ? 'label-danger' : 'label-info';
      default: return 'label-info';
    }
  }

  speedText(port: PortEntry): string {
    return fmtSpeed(port);
  }

  /** The speed under the status pill of the list, without the duplex when
   *  the Duplex column is there to show it. */
  listSpeedText(port: PortEntry): string {
    return fmtSpeed(port, !this.hasHalfDuplex);
  }

  memberOfText(port: PortEntry): string {
    const memberOf = port.member_of;
    if (!memberOf) return '';
    let kindText: string;
    if (memberOf.kind === 'aggregate') {
      kindText = 'Aggregate';
    } else if (memberOf.kind === 'hard-switch') {
      kindText = 'Hardware switch';
    } else {
      kindText = 'Software switch';
    }
    return `${kindText} ${memberOf.name}`;
  }

  slotAria(slot: Slot): string {
    if (!slot.port) return `${slot.name}: no vNIC`;
    const parts = [slot.port.name];
    if (slot.port.alias) {
      parts.push(slot.port.alias);
    }
    parts.push(this.legendText[slot.state].toLowerCase());
    if (slot.port.in_use) {
      parts.push('in use');
    }
    if (slot.issue) {
      parts.push(slot.issue.title);
    }
    return parts.join(', ');
  }

  /** A port whose link down is not an alarm: not in use, or one port of a
   *  switch (not every LAN port has a cable). */
  isSpare(slot: Slot): boolean {
    const port = slot.port;
    if (!port) return false;
    if (!port.in_use) return true;
    return port.member_of?.kind === 'hard-switch' || port.member_of?.kind === 'switch';
  }

  bps = fmtBps;

  trackByFace = (_index: number, group: FaceGroup): string => group.key;
  trackBySlot = (_index: number, slot: Slot): string => slot.name;
  trackByKey = (_index: number, group: TableGroup): string => group.key;
  trackByRow = (_index: number, row: RowVm): string => row.port.name;
}
